import {
    panel,
    PropertiesPanel,
    PropertiesPanelAxisToggle,
    PropertiesPanelLeafConfig,
    PropertiesPanelPickerOption,
    PropertiesPanelSection
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { Spinner } from '../../../../../widgets/spinner';
import { SearchInput } from '../../../../../widgets/input';
import { useDialogManager } from '../../../../../widgets/dialog';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useMaterialsPropertiesCreator } from './materialsPropertiesConfig';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import InspectorShell, { InspectorUsage } from './inspectorShell';
import { CamerasIcon, MeshNodeIcon } from '../../../../../widgets/icons/scene';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import { captureMaterialPreview, materialSwatchFallback, readMaterialPreview, saveMaterialPreview } from './materialPreview';
import { sceneAssetUrl } from './sceneUrls';
import { buildPbrMaterialPanel, filterPbrMaterialPanel } from './buildPbrMaterialPanel';
import { textureMapping } from './textureMapping';
import { buildPbrExtraProperties } from './pbrExtraProperties';
import { FinishMode } from './pbrMaterialLayout';
import { ChevronIcon } from '../../../../../widgets/icons/scene';

const generateTextureUniqueId = <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
    return [material.id, type, mobile ? 'mobile' : 'default'].join('_');
};

interface MaterialOverlaySimpleProps {
    material: any; // Il materiale BABYLON selezionato
    onMaterialUpdated?: (material: any) => void; // Callback per notificare aggiornamenti del materiale
    onNameChanged?: (newName: string) => void; // Callback per notificare cambio nome specificatamente
    additionalFeatures?: any;
    onClose?: () => void; // Callback per chiudere l'overlay
    /** L'anteprima è appena stata riscattata: la lista che ci ha aperti può mostrarla subito. */
    onPreviewUpdated?: (materialID: string, dataUrl: string) => void;
    /** Porta la colonna di sinistra sugli oggetti e apre quello scelto: vedi la pastiglia. */
    onRevealObject?: (meshID: string) => void;
    /** La scena è cambiata con un gesto che non passa da un campo: chi ci sta sopra segni che c'è da salvare. */
    onSceneChanged?: () => void;
}

const UnsupportedNote = styled.p`
    padding: 20px;
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    font-style: italic;
    text-align: center;
`;

// IL VELO SUL RITRATTO. Nero e glifo bianco in tutti e due i temi, e non è una dimenticanza:
// sotto c'è una fotografia, che chiara o scura resta quella — un velo che seguisse il tema
// sparirebbe sulla metà delle sfere.
const PreviewShot = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    opacity: 0;
    transition: opacity 120ms ease;

    svg {
        width: 16px;
        height: 16px;
    }
`;

// IL RITRATTO IN TESTATA È ANCHE IL COMANDO, e sono due cose che si sono unite. La faccia del
// materiale stava due volte nello stesso pannello — una pallina tinta accanto al nome e la sfera
// vera nella riga sotto — e quella accanto al nome, cioè il posto dove si guarda per capire che
// cosa si sta toccando, era un segnaposto.
//
// Rifare la fotografia è un gesto raro — lo si fa dopo aver cambiato il materiale — e una riga
// con etichetta lo annunciava a ogni apertura, prima ancora del colore di base: il posto più in
// vista per la cosa che si fa meno. Adesso il gesto sta sulla cosa: si preme la sfera. Che si
// possa premere lo dice il velo, che compare passandoci sopra e resta acceso mentre scatta — è
// anche l'unico segno che sta succedendo.
const PreviewThumb = styled.button<{ $image: string | null; $color?: string | null }>`
    position: relative;
    flex: none;
    width: 34px;
    height: 34px;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$image ? `url(${props.$image})` : materialSwatchFallback(props.$color))};
    background-position: center;
    background-size: cover;

    &:not(:disabled):hover ${PreviewShot}, &:focus-visible ${PreviewShot}, &[data-busy='true'] ${PreviewShot} {
        opacity: 1;
    }

    &:disabled {
        cursor: default;
    }
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
    z-index: 10000 !important;
`;

// Il titolo di una SEZIONE non si apre e non si chiude (è la regola del prototipo): una sezione
// che si chiude nasconderebbe metà materiale dietro una maniglia che nessuno ha ragione di
// premere. A chiudersi sono i gruppi dentro di lei, che è il livello a cui la scelta serve.
// IL CAPITOLO NON SI DISEGNA PIÙ QUI. Era una `h4` scritta a mano, uguale a quella che World
// ed Effects si scrivevano per conto loro: tre copie della stessa parola in alto, con tre
// margini che avevano già cominciato a divergere. Ora è `PropertiesPanelSection`.

// LA BARRA STA IN CIMA AL PANNELLO, non dentro un capitolo: cerca fra TUTTI i parametri del
// materiale — anche quelli che stanno sotto la spina «Advanced» — e un campo messo dentro una
// card prometterebbe di cercare solo lì dentro.
const ParameterSearch = styled.div`
    margin: 0 12px 12px 0;
`;

// IL VUOTO RISPONDE. Un pannello che si svuota senza dire niente si legge come un pannello
// rotto, e chi cerca non sa se ha sbagliato parola o se quel parametro qui non esiste.
const NoMatch = styled.p`
    margin: 4px 12px 0 2px;
    color: ${panel.inkMuted};
    font-size: 12px;
`;

// LA SPINA «ADVANCED» NON È UN ACCORDION, ED È UNA SCELTA DI SOSTANZA. L'accordion di questo
// backoffice tiene il corpo dentro un contenitore alto al massimo 3000px con `overflow: hidden`:
// dieci gruppi aperti là dentro si troverebbero il fondo tagliato, senza niente che lo dica.
// Qui la spina decide **se** i gruppi vengono disegnati, e chi li disegna è la stessa colonna
// che disegna Surface — nessun contenitore in mezzo, nessun tetto da rispettare.
// «ADVANCED» E UN CAPITOLO CHE SI APRE, e si legge come gli altri capitoli: stessa misura,
// stesso peso, stesso grigio. Prima era una riga in grassetto sopra una linea, cioe un terzo
// livello di titolo in una colonna che ne ha gia due — e chi scorreva non sapeva se stava
// leggendo un capitolo o una card.
const AdvancedSpine = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin-top: 14px;
    border: 0;
    padding: 0 2px 6px;
    background: transparent;
    color: ${panel.inkMuted};
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;

    svg {
        width: 10px;
        height: 10px;
        transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
        transition: transform 120ms ease;
    }
`;

// SI RICORDA PER MATERIALE, e vive fuori dal componente perché il componente si rimonta a ogni
// cambio di soggetto: tenuto nello stato, «Advanced» si richiuderebbe sotto le dita ogni volta
// che si torna su un materiale che si era appena aperto.
const advancedOpenByMaterial = new Map<string, boolean>();

// IL FLUSSO SCELTO A MANO SI RICORDA, per la stessa ragione. La modalità si DEDUCE dal
// materiale (vedi `detectFinishMode`), ma passare a Separate su un materiale che non ha ancora
// caricato niente non lascia traccia su di lui: senza questa memoria la card tornerebbe a ORM
// al primo ridisegno, sotto le dita di chi sta per caricare la prima mappa.
const finishModeByMaterial = new Map<string, FinishMode>();

// E SI RICORDA ANCHE CIÒ CHE SI STACCA cambiando sponda, per rimetterlo se si torna indietro.
//
// CAMBIARE VISTA NON TOCCA IL MATERIALE quando di là non c'è ancora niente da rimettere
// (utente, 2026-09-14). Prima si staccava sempre: dopo un ricaricamento della pagina la
// memoria riparte vuota, e un clic di curiosità sull'altra sponda svuotava gli slot e
// anneriva il modello — le mappe erano recuperabili solo tornando indietro, e un salvataggio
// fatto lì in mezzo se le portava via davvero.
//
// Le due sponde però non possono essere accese INSIEME: lo shader moltiplica in fila — la
// ruvidità presa dal verde del pacchetto e poi di nuovo dalla mappa separata — e il materiale
// diventerebbe lucido senza che si capisca perché. Per questo il flusso si sceglie davvero
// nei due momenti in cui si tocca il materiale sul serio: quando si carica una mappa, e al
// SALVATAGGIO — dove resta solo l'ultima scelta e il resto viene via (vedi
// `commitFinishChoices`, chiamata da `SceneEditor`).
//
// E IL CAMBIO NON CHIEDE CONFERMA (utente, 2026-09-09). Una c’era, e contava le mappe che si
// staccavano: ma nella sponda dove si arriva quelle mappe non ci sono mai state — la loro
// casella è vuota — e tornando indietro ricompaiono. Una domanda che nel caso normale non
// protegge da niente insegna solo a rispondere «sì» senza leggere.

type FinishSide = Record<string, unknown>;
const finishMemoryByMaterial = new Map<string, Partial<Record<FinishMode, FinishSide>>>();

// LA STESSA IMMAGINE, anche dopo un ricaricamento: in sessione i due slot tengono lo stesso oggetto,
// riletti dal salvataggio sono due texture con lo stesso indirizzo.
const sameImage = (a: any, b: any) => Boolean(a && b && (a === b || (a.url && a.url === b.url)));

// IL FLUSSO SI LEGGE DAI FLAG, PRIMA CHE DALLE MAPPE. Il verde, l'alfa o il rosso accesi sulla mappa
// del metallo dicono che è un pacchetto, e così un'occlusione presa dalla stessa immagine. Guardare
// solo se c'era un'ambient sbagliava proprio sul caso più comune — un glTF importato porta la stessa
// ORM anche come ambient, per l'occlusione — e il materiale si apriva in Separate, con la ORM nello
// slot «Metallic».
//
// IL BLU DA SOLO NON FA UN PACCHETTO (utente, 2026-09-15). Nelle separate è il canale da cui si legge
// la mappa del metallo — «Texture channel · Gray | B» — e un pacchetto senza la ruvidità nel verde
// non è la forma in cui una ORM arriva.
const detectFinishMode = (material: any): FinishMode => {
    const metal = material?.metallicTexture;
    const packed =
        metal &&
        (material.useRoughnessFromMetallicTextureGreen ||
            material.useRoughnessFromMetallicTextureAlpha ||
            material.useAmbientOcclusionFromMetallicTextureRed ||
            sameImage(material.ambientTexture, metal));

    if (packed) return 'orm';
    return material?.microSurfaceTexture || material?.ambientTexture || (metal && material.useMetallnessFromMetallicTextureBlue) ? 'separate' : 'orm';
};

/** Il flusso di un materiale: quello scelto a mano, se c'è, altrimenti quello che si legge. */
const modeOf = (material: any): FinishMode => finishModeByMaterial.get(material?.id ?? material?.name ?? '') ?? detectFinishMode(material);

// UN PACCHETTO SI RICONOSCE GUARDANDOLO (utente, 2026-09-14). Lo slot del pacchetto e quello
// della mappa metallica sono lo stesso campo del materiale: passando alla vista ORM, la mappa
// del metallo caricata in Separate si vedeva lì dentro come se fosse una ORM, e non lo è.
//
// La differenza è nei pixel, non nel nome: una mappa del solo metallo è in scala di grigi — i tre
// canali portano lo stesso numero — mentre in un pacchetto occlusione, ruvidità e metallo sono
// tre immagini diverse. Quindi si leggono i pixel e si conta quanti hanno i canali davvero
// diversi; una manciata sparsa è rumore di compressione, non un pacchetto.
//
// La lettura passa dalla GPU, costa e si fa una volta sola per texture: l'esito resta qui, legato
// alla texture stessa, e se ne va con lei.
const CHANNEL_NOISE = 4;
const PACKED_MIN_SHARE = 0.01;
const PACKED_SAMPLES = 20000;

const packedByTexture = new WeakMap<object, boolean>();
const packedPending = new WeakSet<object>();

/** true se la mappa ha più canali, false se è in scala di grigi, undefined se non si sa ancora. */
const isPackedTexture = (texture: any): boolean | undefined => (texture ? packedByTexture.get(texture) : undefined);

const readPackedTexture = (texture: any, whenKnown: () => void) => {
    if (!texture || packedByTexture.has(texture) || packedPending.has(texture)) return;
    if (typeof texture.readPixels !== 'function') return;
    if (typeof texture.isReady === 'function' && !texture.isReady()) return;

    let reading: unknown;
    try {
        reading = texture.readPixels();
    } catch {
        return;
    }
    if (!reading) return;

    packedPending.add(texture);
    Promise.resolve(reading)
        .then((data: any) => {
            if (!data || !data.length) return;
            const pixels = Math.floor(data.length / 4);
            const step = Math.max(1, Math.floor(pixels / PACKED_SAMPLES)) * 4;
            let different = 0;
            let sampled = 0;
            for (let i = 0; i + 2 < data.length; i += step) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (Math.abs(r - g) > CHANNEL_NOISE || Math.abs(g - b) > CHANNEL_NOISE || Math.abs(r - b) > CHANNEL_NOISE) different++;
                sampled++;
            }
            if (sampled > 0) packedByTexture.set(texture, different / sampled > PACKED_MIN_SHARE);
        })
        .catch(() => undefined)
        .finally(() => {
            packedPending.delete(texture);
            whenKnown();
        });
};

/**
 * LO SLOT DEL PACCHETTO È VUOTO anche quando una mappa c'è, se quella mappa non è un pacchetto:
 * è la mappa del metallo delle Separate, che in questa vista non vuol dire niente. Finché la
 * lettura non ha risposto si mostra ciò che c'è, per non far lampeggiare la riga.
 */
const packSlotIsEmpty = (material: any) => !material?.metallicTexture || isPackedTexture(material.metallicTexture) === false;

// LE RIGHE DELLO SLOT, SVUOTATE. La prima `metallic-image` è la mappa e resta, senza miniatura né
// cestino, perché è da lì che si carica un pacchetto; la seconda è la versione mobile e non si
// disegna, come quando lo slot è vuoto davvero. Con loro se ne vanno scalatura e canale UV, che
// senza mappa non governano niente. L'ordine — prima la mappa, poi la mobile — è quello in cui il
// pannello le costruisce.
const hiddenPackRows = () => {
    let seen = 0;
    return {
        'metallic-image': (property: any) => (seen++ === 0 ? { ...property, images: null } : null),
        'metallic-texture-scaling': () => null,
        'metallic-uv-channel': () => null
    };
};

// L'OCCLUSIONE DEL PACCHETTO SI DICE COME LA DICE IL glTF: la stessa immagine anche nello slot
// ambient, letta in grigio — cioè dal rosso. Si faceva col flag del rosso, che a vedersi è
// identico, ma quel flag lo legge solo lo shader: l'export lo ignora, e l'AR partiva senza
// occlusione. Lo slot ambient invece il glTF lo conosce, e con la stessa immagine ne esce la sua
// forma canonica — una texture sola, per la ruvidità, il metallo e l'occlusione.
//
// Il flag del rosso resta letto, per i materiali salvati prima: la lettera li mostra accesi, e
// toccarla li porta nella forma nuova.
//
// L'OCCLUSIONE È DEL PACCHETTO SOLO SE È LA SUA IMMAGINE. Cambiare vista non stacca niente, e nella
// vista ORM la mappa AO delle separate accendeva la R di un pacchetto che non c'era.
const ormOcclusionOn = (material: any) =>
    Boolean(material?.useAmbientOcclusionFromMetallicTextureRed || sameImage(material?.ambientTexture, material?.metallicTexture));

const setOrmOcclusion = (material: any, on: boolean) => {
    material.useAmbientOcclusionFromMetallicTextureRed = false;
    material.ambientTexture = on ? (material.metallicTexture ?? null) : null;
    if (on) material.useAmbientInGrayScale = true;
};

// LE DUE SPONDE, DICHIARATE. `metallicTexture` sta in tutte e due e non è una svista: nel
// pacchetto è la mappa con i tre canali, in Separate è la mappa del solo metallo. Stesso campo, due
// significati — e proprio per questo va staccato passando di là. Lo stesso vale per `ambientTexture`:
// nel pacchetto è la ORM stessa, letta per l'occlusione (vedi `setOrmOcclusion`); in Separate è la
// mappa dell'occlusione. E per i due flag che dicono da quale canale leggerle: il blu è la B del
// pacchetto o il canale della mappa del metallo, il grigio dell'ambient è la R dell'una o dell'altra.
const FINISH_SIDES: Record<FinishMode, string[]> = {
    orm: [
        'metallicTexture',
        'useRoughnessFromMetallicTextureGreen',
        'useRoughnessFromMetallicTextureAlpha',
        'useMetallnessFromMetallicTextureBlue',
        'useAmbientOcclusionFromMetallicTextureRed',
        'ambientTexture',
        'useAmbientInGrayScale'
    ],
    separate: ['metallicTexture', 'microSurfaceTexture', 'ambientTexture', 'useAmbientInGrayScale', 'useMetallnessFromMetallicTextureBlue']
};

// I CAMPI DI UNA SPONDA SOLA: il verde, l'alfa e il rosso sono del pacchetto, la mappa della
// ruvidità è delle separate. Gli altri stanno in tutte e due — uno slot, una sola immagine, un
// canale da cui leggerla — e sono quelli che non si possono tenere doppi.
const other = (mode: FinishMode): FinishMode => (mode === 'orm' ? 'separate' : 'orm');
const FINISH_ONLY: Record<FinishMode, string[]> = {
    orm: FINISH_SIDES.orm.filter((field) => !FINISH_SIDES.separate.includes(field)),
    separate: FINISH_SIDES.separate.filter((field) => !FINISH_SIDES.orm.includes(field))
};

const readFinishSide = (material: any, mode: FinishMode): FinishSide => Object.fromEntries(FINISH_SIDES[mode].map((field) => [field, material?.[field]]));

const writeFinishSide = (material: any, mode: FinishMode, side?: FinishSide) => {
    FINISH_SIDES[mode].forEach((field) => {
        const value = side ? side[field] : undefined;
        material[field] = value ?? (field.endsWith('Texture') ? null : false);
    });
};

// SCEGLIERE UN FLUSSO VUOL DIRE TOGLIERE L'ALTRO — solo ciò che è suo soltanto, perché gli slot
// condivisi sono già occupati da quello scelto. Quel che si toglie resta in memoria, così finché
// la pagina è aperta tornare indietro rimette tutto.
//
// MA UN FLUSSO VUOTO NON SCEGLIE NIENTE (utente, 2026-09-14). Guardare come sarebbe di là è un
// gesto gratis, e deve restarlo anche se poi si salva: se la sponda scelta non porta una mappa
// sua, non c'è nessuna scelta da rispettare e quella di prima resta dov'è. Senza questa riga un
// clic di curiosità seguito da un salvataggio cancellava la mappa della ruvidità.
const finishSideHasContent = (material: any, mode: FinishMode) => (mode === 'orm' ? !packSlotIsEmpty(material) : Boolean(material?.microSurfaceTexture));

const commitFinishChoice = (material: any, mode: FinishMode) => {
    const fields = FINISH_ONLY[other(mode)];
    if (!material || !fields.some((field) => material[field])) return false;
    if (!finishSideHasContent(material, mode)) return false;

    const key = material.id ?? material.name ?? '';
    const remembered = finishMemoryByMaterial.get(key) ?? {};
    remembered[other(mode)] = { ...(remembered[other(mode)] ?? {}), ...Object.fromEntries(fields.map((field) => [field, material[field]])) };
    finishMemoryByMaterial.set(key, remembered);

    fields.forEach((field) => (material[field] = field.endsWith('Texture') ? null : false));
    return true;
};

/**
 * AL SALVATAGGIO UN MATERIALE HA UN FLUSSO SOLO (utente, 2026-09-14: «ripulire il superfluo e
 * lasciare solo l'ultima scelta»). Durante il lavoro le due sponde possono convivere — una
 * attaccata e una in memoria — perché cambiare vista non deve distruggere niente; ma ciò che
 * finisce sul server è la scelta fatta per ultima, e le mappe rimaste fuori dagli slot il core
 * le pota da sé.
 *
 * Tocca solo i materiali su cui il flusso è stato scelto a mano: gli altri sono come sono stati
 * caricati, e non spetta a noi riordinarli.
 */
export const commitFinishChoices = (materials: any[]) => {
    materials?.forEach((material) => {
        const mode = finishModeByMaterial.get(material?.id ?? material?.name ?? '');
        if (mode) commitFinishChoice(material, mode);
    });
    finishMemoryByMaterial.clear();
};

type FinishOverrides = Record<string, (property: PropertiesPanelLeafConfig) => PropertiesPanelLeafConfig | null>;

// LE LETTERE DEI CANALI, costruite qui e non nel generatore. Il generatore ha tre spunte con la frase
// intera — «Use roughness from green» e compagne —: nel mockup sono le R G B sulle righe dei tre valori
// del pacchetto. E le mappe separate hanno un «Texture channel» che il generatore non ha affatto.
// Toccarle vuol dire toccare il flusso, e il flusso lo conosce questo file.
//
// SENZA LA MAPPA CHE LE ALIMENTA LE LETTERE SI SPENGONO, e con loro il valore dell'occlusione (mockup:
// `gate` e `dim`). Ruvidità e metallo restano vivi: una superficie è ruvida o metallica anche senza una
// mappa che lo moduli, mentre l'occlusione è l'ombra che la geometria si fa addosso — senza una mappa
// che la descriva non c'è niente da dosare.
//
// SONO PULSANTI, e un pulsante non emette `change`: il «da salvare» lo segna `changed`.
const finishRows = (material: any, mode: FinishMode, changed: () => void): { overrides: FinishOverrides; extra: PropertiesPanelLeafConfig[] } => {
    const setFlag = (field: string, on: boolean) => {
        material[field] = on;
        changed();
    };

    if (mode === 'orm') {
        const noPack = packSlotIsEmpty(material);
        const lettered =
            (tag: PropertiesPanelAxisToggle, dim = false) =>
            (property: PropertiesPanelLeafConfig): PropertiesPanelLeafConfig =>
                property.type === 'range' ? { ...property, tag: { ...tag, disabled: noPack }, disabled: property.disabled || (dim && noPack) } : property;

        return {
            extra: [],
            overrides: {
                'ambient-strength': lettered(
                    {
                        id: 'orm-occlusion-red',
                        axis: 'x',
                        letter: 'R',
                        ariaLabel: T._('Occlusion from the red channel', 'Admin'),
                        pressed: ormOcclusionOn(material),
                        onChange: (on) => {
                            setOrmOcclusion(material, on);
                            changed();
                        }
                    },
                    true
                ),
                roughness: lettered({
                    id: 'orm-roughness-green',
                    axis: 'y',
                    letter: 'G',
                    ariaLabel: T._('Roughness from the green channel', 'Admin'),
                    pressed: Boolean(material.useRoughnessFromMetallicTextureGreen),
                    onChange: (on) => setFlag('useRoughnessFromMetallicTextureGreen', on)
                }),
                metallic: lettered({
                    id: 'orm-metallic-blue',
                    axis: 'z',
                    letter: 'B',
                    ariaLabel: T._('Metallic from the blue channel', 'Admin'),
                    pressed: Boolean(material.useMetallnessFromMetallicTextureBlue),
                    onChange: (on) => setFlag('useMetallnessFromMetallicTextureBlue', on)
                })
            }
        };
    }

    // «GRAY | R» E «GRAY | B» SONO DUE FLAG DI BABYLON, LETTI PER QUELLO CHE FANNO (verificato nello
    // shader, 5.50.1). `useAmbientInGrayScale`, nonostante il nome, prende l'occlusione dal solo rosso;
    // spento moltiplica l'immagine intera, che su una mappa in grigio è il grigio. Il blu legge il metallo
    // dal blu, spento dal rosso — di nuovo il grigio, su una mappa in grigio.
    //
    // LA RUVIDITÀ HA LA SUA RIGA, MA LA G NON SI SCEGLIE (utente, 2026-09-15). Il foglio chiede
    // «Grey / G», e lo shader legge quella mappa soltanto dal rosso: nessun flag lo cambia. La G resta in
    // vista e dice perché — su una mappa in grigio, Gray è già la lettura giusta.
    const textureChannel = (row: {
        id: string;
        /** Il flag che legge dal canale. Senza, la lettera c'è ma non si sceglie: vedi `reason`. */
        field?: string;
        hasMap: boolean;
        axis: 'x' | 'y' | 'z';
        letter: string;
        channel: string;
        reason?: string;
    }): PropertiesPanelLeafConfig => {
        const field = row.field;
        const reads = field ? Boolean(material[field]) : false;
        return {
            type: 'axis-toggles',
            id: row.id,
            label: T._('Texture channel', 'Admin'),
            exclusive: true,
            disabled: !row.hasMap,
            toggles: [
                {
                    id: `${row.id}-gray`,
                    letter: T._('Gray', 'Admin'),
                    ariaLabel: T._('Greyscale', 'Admin'),
                    pressed: !reads,
                    onChange: field ? () => setFlag(field, false) : undefined
                },
                {
                    id: `${row.id}-${row.axis}`,
                    axis: row.axis,
                    letter: row.letter,
                    ariaLabel: row.channel,
                    pressed: reads,
                    reason: row.reason,
                    onChange: field ? () => setFlag(field, true) : undefined
                }
            ]
        };
    };

    return {
        overrides: {
            'ambient-strength': (property) => (property.type === 'range' ? { ...property, disabled: property.disabled || !material.ambientTexture } : property)
        },
        extra: [
            textureChannel({
                id: 'ambient-texture-channel',
                field: 'useAmbientInGrayScale',
                hasMap: Boolean(material.ambientTexture),
                axis: 'x',
                letter: 'R',
                channel: T._('Red channel', 'Admin')
            }),
            textureChannel({
                id: 'roughness-texture-channel',
                hasMap: Boolean(material.microSurfaceTexture),
                axis: 'y',
                letter: 'G',
                channel: T._('Green channel', 'Admin'),
                reason: T._('Not available: the roughness map is always read from its red channel', 'Admin')
            }),
            textureChannel({
                id: 'metallic-texture-channel',
                field: 'useMetallnessFromMetallicTextureBlue',
                hasMap: Boolean(material.metallicTexture),
                axis: 'z',
                letter: 'B',
                channel: T._('Blue channel', 'Admin')
            })
        ]
    };
};

const MaterialOverlaySimple: React.FC<MaterialOverlaySimpleProps> = ({
    material,
    onMaterialUpdated,
    onNameChanged,
    additionalFeatures,
    onClose,
    onPreviewUpdated,
    onRevealObject,
    onSceneChanged
}) => {
    const [zkProduct, zkScene, sceneManager, sceneEditor, designManager] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneManager,
        x.sceneEditor,
        x.designManager
    ]);

    // Spostato getTextureUrls dentro il componente per accedere a zkScene
    // OGNI STRADA PORTA A UN INDIRIZZO RELATIVO ALLA SCENA, non solo l’ultima. Le prime
    // quattro tornavano la stringa così com’era — il percorso dentro la cartella della scena —
    // e il browser la risolveva contro il backoffice: da lì le miniature rotte accanto a
    // texture che in 3D si vedono benissimo. La risoluzione è una sola e vale per tutte.
    const getTextureUrls = useCallback(
        (texture: BABYLON.BaseTexture): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const resolve = (url: string) => sceneAssetUrl(rootUrl, url);
            const isTextureWithFiles = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { _files: string[] } =>
                '_files' in texture && !!texture._files && Array.isArray(texture._files);

            const isTextureWithUrl = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { url: string } => 'url' in texture && !!texture.url;

            if (isTextureWithFiles(texture) && texture._files.length > 1) {
                return texture._files.map(resolve);
            }
            if (texture.metadata && texture.metadata.fileUrl) {
                return [resolve(texture.metadata.fileUrl)];
            }
            if (isTextureWithUrl(texture) && texture.url.startsWith('data:')) {
                return [texture.url];
            }
            if (texture.getInternalTexture && texture.getInternalTexture()?.url) {
                const textureUrl = texture.getInternalTexture()?.url;
                return textureUrl ? [resolve(textureUrl)] : [];
            }
            if (isTextureWithUrl(texture) && texture.url) {
                // Check if materialMapping exists before accessing its properties
                if (
                    (texture as any).materialMapping &&
                    (texture as any).materialMapping.libraryMaterialID &&
                    (texture as any).materialMapping.libraryMaterialRootUrl
                ) {
                    const materialMapping = (texture as any).materialMapping;
                    const idx = texture.url.indexOf(materialMapping.libraryMaterialID.replace(/-/g, ''));
                    if (idx !== -1) return [materialMapping.libraryMaterialRootUrl + texture.url];
                }
                return [resolve(texture.url)];
            }
            return [];
        },
        [zkScene]
    );

    const [loadingTextures, setLoadingTextures] = useState<Record<string, boolean>>({});
    const [, setUpdate] = useState(false);
    const materialsPropertiesCreator = useMaterialsPropertiesCreator();
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // L'anteprima del materiale aperto. Si riscatta solo quando qualcuno lo chiede:
    // rigenerarla a ogni modifica di proprietà era il disegno di prima, ed è il motivo per cui
    // l'avevano spenta — ogni scatto smonta e rimonta l'environment della scena, e farlo
    // mentre trascini uno slider è insostenibile.
    const [preview, setPreview] = useState<string | null>(null);
    const [isCapturingPreview, setIsCapturingPreview] = useState(false);

    useEffect(() => {
        setPreview(material?.id ? readMaterialPreview(sceneEditor, material.id) : null);
    }, [material?.id, sceneEditor]);

    // Due passi e non uno: lo scatto si mostra appena c'è, e se il caricamento non riesce lo
    // si dice. L'hai chiesto tu, quindi hai diritto sia di vederlo sia di sapere che non
    // sopravviverà al salvataggio.
    const capturePreview = useCallback(async () => {
        if (!sceneEditor || !material?.id || isCapturingPreview) return;

        setIsCapturingPreview(true);
        const dataUrl = await captureMaterialPreview(sceneEditor, material.id);

        if (!dataUrl) {
            setIsCapturingPreview(false);
            showErrorNotification({ message: T._('Could not generate the material preview.', 'Admin') });
            return;
        }

        setPreview(dataUrl);
        onPreviewUpdated?.(material.id, dataUrl);

        const url = await saveMaterialPreview(sceneEditor, material.id, dataUrl);
        setIsCapturingPreview(false);

        if (!url) showErrorNotification({ message: T._('The preview was generated but could not be saved.', 'Admin') });
    }, [sceneEditor, material, isCapturingPreview, onPreviewUpdated]);

    const renderDebounced = useCallback(
        debounce(() => designManager?.render(), 100),
        [designManager]
    );

    const updateState = useCallback(
        <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => {
            const isDesignMaterial = zkProduct
                ?.get('scenes')
                .at(0)
                .get('meshSides')
                ?.toArray()
                .some((meshSide) => meshSide.get('materialID') === material.id);

            // Always re render because the base design material uses the same metallic/roughness of the base material
            if (isDesignMaterial) {
                renderDebounced();
            }

            setUpdate((prev) => !prev);
            sceneEditor?.render();
        },
        [sceneManager, zkProduct, renderDebounced, sceneEditor]
    );

    const isTextureLoading = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            return loadingTextures[generateTextureUniqueId(material, type, mobile)];
        },
        [loadingTextures]
    );

    const uploadTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (files.length === 0) {
                throw new Error('No files selected');
            }

            if (type === 'reflection' && !additionalFeatures?.customEnvironmentTexture) {
                // Chiudo l'overlay prima di mostrare la dialog
                if (onClose) {
                    onClose();
                }

                showDialog(
                    'Upgrade_Plan',
                    <UpgradeBanner
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            const uniqueId = generateTextureUniqueId(material, type, mobile);
            setLoadingTextures((prev) => ({ ...prev, [uniqueId]: true }));

            // SE LA MAPPA VA NEL PACCHETTO si decide prima di caricarla: dopo c'è già, e non si
            // saprebbe più se è arrivata in uno slot vuoto o al posto di un'altra.
            const pbr = material as any;
            const intoPack = type === 'metallic' && !mobile && modeOf(pbr) === 'orm';
            // Uno slot che si VEDE vuoto si comporta da vuoto: se lì dentro c'era la mappa del solo
            // metallo, la card non la mostrava, e la ORM che arriva adesso è la prima del pacchetto.
            const packWasEmpty = intoPack && packSlotIsEmpty(pbr);
            const replaced: any[] = intoPack ? [pbr.metallicTexture, pbr.ambientTexture] : [];
            // UNA MAPPA SEPARATA IN UNO SLOT VUOTO SI LEGGE IN GRIGIO, che è il default del foglio di
            // mappatura; al posto di un'altra mappa resta il canale che era stato scelto.
            const separateWasEmpty =
                !mobile && modeOf(pbr) === 'separate' && ((type === 'ambient' && !pbr.ambientTexture) || (type === 'metallic' && !pbr.metallicTexture));

            try {
                if (mobile) {
                    await sceneEditor.loadMobileTextureFilesAsync(Array.from(files), material, type, false);
                } else {
                    await sceneEditor.loadTextureFilesAsync(Array.from(files), material, type, false);
                }

                // CARICARE UNA MAPPA È SCEGLIERE IL FLUSSO: da qui in poi le due sponde non possono
                // più convivere sul materiale, o lo shader moltiplicherebbe due ruvidità.
                commitFinishChoice(pbr, modeOf(pbr));

                if (separateWasEmpty && type === 'ambient') pbr.useAmbientInGrayScale = false;
                if (separateWasEmpty && type === 'metallic') pbr.useMetallnessFromMetallicTextureBlue = false;

                if (intoPack) {
                    // UNA ORM IN UNO SLOT VUOTO ARRIVA CON I TRE CANALI ACCESI, perché è questo che
                    // vuol dire ORM: occlusione nel rosso, ruvidità nel verde, metallo nel blu. Con le
                    // spunte spente lo shader leggeva il metallo dal rosso e ignorava il verde — e
                    // l'export, che le spunte non le guarda, mostrava in AR un altro materiale.
                    // Al posto di un'altra mappa, invece, restano le spunte scelte, e l'occlusione, se
                    // c'era, passa sulla mappa nuova.
                    if (packWasEmpty) {
                        pbr.useRoughnessFromMetallicTextureGreen = true;
                        pbr.useMetallnessFromMetallicTextureBlue = true;
                        pbr.useRoughnessFromMetallicTextureAlpha = false;
                        setOrmOcclusion(pbr, true);
                    } else if (ormOcclusionOn(pbr)) {
                        setOrmOcclusion(pbr, true);
                    }

                    // La mappa di prima il motore la tiene in vita finché sta nello slot ambient: ora
                    // che non la usa più nessuno, si libera.
                    replaced.forEach((texture) => {
                        if (texture && sceneEditor.scene?.textures?.includes(texture) && !sceneEditor.isTextureUsed(texture)) texture.dispose();
                    });
                }
            } catch (error) {
                console.error(error);
                throw new Error('Failed to upload texture', {
                    cause: error
                });
            } finally {
                setLoadingTextures((prev) => ({ ...prev, [uniqueId]: false }));
                updateState(material);
                onSceneChanged?.();
            }
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    const removeTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            // TOGLIERE LA ORM TOGLIE ANCHE LA SUA OCCLUSIONE: nello slot ambient c'è la stessa
            // immagine, e da sola resterebbe un'AO senza più la mappa da cui viene. Il flusso si
            // legge prima di togliere: dopo, senza la mappa, un'ambient rimasta direbbe Separate.
            const fromPack = type === 'metallic' && !mobile && modeOf(material) === 'orm';

            if (mobile) {
                sceneEditor.removeMaterialMobileTexture(material, type);
            } else {
                sceneEditor.removeMaterialTexture(material, type);
                if (fromPack && (material as any).ambientTexture) sceneEditor.removeMaterialTexture(material, 'ambient');
            }

            updateState(material);

            // IL CESTINO È UN PULSANTE, e un pulsante non emette `change`: togliere una mappa
            // cambiava il materiale senza che nessuno lo segnasse da salvare.
            onSceneChanged?.();
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    // DOVE STA LA VERSIONE MOBILE DI UNA TEXTURE, e sono due posti soli: `metadata.mobileUrl` per
    // un'immagine, `metadata.mobileFiles` per una cube map. Li riempiono `setMobileTextureImage` e
    // `setMobileCubeTextureImages` del motore, e un terzo posto non esiste — per questo la ricerca
    // finisce qui invece di continuare altrove.
    //
    // SI RISOLVE COME QUELLA DESKTOP, ed è il punto: la mappa desktop ha cinque modi di trovare la
    // propria immagine e la mobile ne aveva uno, con una regola tutta sua — «se non comincia per
    // http, incollaci davanti la radice». Un `blob:` o un `data:`, che è la forma di una texture
    // appena caricata e non ancora salvata, diventavano così un indirizzo che non esiste: nessuna
    // anteprima, e nessun modo di capire perché. La radice ora la mette `sceneAssetUrl`, che
    // riconosce lo schema — ed è la stessa che risolve la mappa desktop.
    //
    // (Resta aperto di là il caso dei materiali importati dalla libreria, la cui versione mobile
    //  vuole la radice del materiale e non quella della scena: https://www.wrike.com/open.htm?id=1712281262)
    const getMobileTextureUrls = useCallback(
        (texture: any): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const files: string[] = texture?.metadata?.mobileFiles ?? [];

            if (files.length > 0) {
                return files.map((file) => sceneAssetUrl(rootUrl, file));
            }

            const url: string | undefined = texture?.metadata?.mobileUrl;
            return url ? [sceneAssetUrl(rootUrl, url)] : [];
        },
        [zkScene]
    );

    const getTexturePreviewUrl = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor || !zkScene) {
                return null;
            }

            const texture = sceneEditor.getMaterialTexture(material, type);
            if (!texture) {
                return null;
            }

            const urls = mobile ? getMobileTextureUrls(texture) : getTextureUrls(texture);

            return urls[0] ?? null;
        },
        [sceneEditor, zkScene, getTextureUrls, getMobileTextureUrls]
    );

    // Funzione stabile per l'onChange del nome materiale
    const handleMaterialNameChange = useCallback(
        (newValue: string) => {
            if (!material) {
                return;
            }

            // Aggiorna il nome direttamente sul materiale BABYLON
            material.name = newValue || 'Material';

            // Chiama updateState per aggiornare l'UI dell'overlay
            updateState(material);

            // Notifica immediatamente il cambiamento del nome al parent
            if (onNameChanged) {
                onNameChanged(material.name);
            }

            // Notifica il sistema che i materiali sono stati aggiornati per sincronizzare l'albero
            if (onMaterialUpdated) {
                setTimeout(() => {
                    onMaterialUpdated(material);
                }, 50); // Small delay to ensure material name is fully updated
            }
        },
        [material, updateState, onMaterialUpdated, onNameChanged]
    );

    // Helpers per il materialsPropertiesCreator
    const helpers = useMemo(() => {
        if (!sceneEditor || !sceneManager) {
            return null;
        }
        return {
            sceneEditor,
            sceneManager,
            updateState,
            isTextureLoading,
            uploadTexture,
            removeTexture,
            getTexturePreviewUrl,
            handleMaterialNameChange
        };
    }, [sceneEditor, sceneManager, updateState, isTextureLoading, uploadTexture, removeTexture, getTexturePreviewUrl, handleMaterialNameChange]);

    // La spina «Advanced» si ricorda di essere aperta, e la memoria è per materiale: aprirla su
    // uno non dice niente su quello che si guarderà dopo.
    const materialKey = material?.id ?? material?.name ?? '';
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => setAdvancedOpen(advancedOpenByMaterial.get(materialKey) ?? false), [materialKey]);

    const toggleAdvanced = useCallback(() => {
        setAdvancedOpen((open) => {
            advancedOpenByMaterial.set(materialKey, !open);
            return !open;
        });
    }, [materialKey]);

    const [finishMode, setFinishMode] = useState<FinishMode>('orm');

    useEffect(() => setFinishMode(modeOf(material)), [materialKey]);

    // LA RICERCA È DI QUESTO MATERIALE, e con lui finisce: cambiando soggetto il campo si
    // svuota. Portarsi dietro la parola di prima vorrebbe dire aprire un materiale nuovo già
    // filtrato, e sembrerebbe un materiale con tre parametri.
    const [parameterFilter, setParameterFilter] = useState('');
    useEffect(() => setParameterFilter(''), [materialKey]);

    // La mappa nello slot del pacchetto si guarda solo quando la vista ORM la mostra: è una
    // lettura dalla GPU, e su una scena intera non vale la pena farla per materiali che nessuno
    // sta aprendo. Quando la risposta arriva, la card si ridisegna.
    const [, redrawAfterRead] = useReducer((count: number) => count + 1, 0);
    useEffect(() => {
        if (finishMode === 'orm') readPackedTexture((material as any)?.metallicTexture, redrawAfterRead);
    }, [finishMode, materialKey, (material as any)?.metallicTexture]);

    const changeFinishMode = useCallback(
        (next: FinishMode) => {
            if (next === finishMode) return;

            const previous: FinishMode = finishMode;
            const remembered = finishMemoryByMaterial.get(materialKey) ?? {};

            // Di là c'è qualcosa da rimettere: si fa lo scambio vero, e questa sponda va in memoria.
            // Se invece di là non c'è niente, il materiale resta com'è e cambia solo la vista: quel
            // che è caricato si vede ancora, e nessuno perde niente per aver guardato.
            if (remembered[next]) {
                remembered[previous] = readFinishSide(material, previous);
                finishMemoryByMaterial.set(materialKey, remembered);

                writeFinishSide(material, previous, undefined);
                writeFinishSide(material, next, remembered[next]);
            }

            // IL CURSORE È UN GUADAGNO, non un valore assoluto: moltiplica la mappa, quindi a zero
            // la mappa non si vede e sembra rotta. Entrando in Separate la ruvidità parte da 1, che
            // è il valore neutro di una moltiplicazione (deciso in call, 2026-09-09).
            if (next === 'separate' && !material.roughness) material.roughness = 1;

            finishModeByMaterial.set(materialKey, next);
            setFinishMode(next);
            updateState(material, true);

            // IL COMMUTATORE È FATTO DI PULSANTI, e un pulsante non emette `change`: il pennarello
            // «da salvare» non lo sentiva. Ma cambiare flusso stacca delle mappe dal materiale, e
            // senza segnarlo «Save» rispondeva «nulla da salvare».
            onSceneChanged?.();
        },
        [finishMode, material, materialKey, updateState, onSceneChanged]
    );

    // Funzione per verificare se il materiale è Standard (non supportato)
    const isStandardMaterial = useCallback((material: any) => {
        // Un materiale Standard ha diffuseColor ma NON ha proprietà PBR avanzate
        return (
            material &&
            material.diffuseColor !== undefined &&
            (material.clearCoat === undefined || !material.clearCoat?.isEnabled) &&
            (material.metallic === undefined || material.metallic === 0) &&
            (material.roughness === undefined || material.roughness === 0) &&
            // Oppure verifica il tipo direttamente se disponibile
            (material instanceof BABYLON?.StandardMaterial || material.getClassName?.() === 'StandardMaterial')
        );
    }, []);

    if (!material || !helpers) {
        return (
            <InspectorShell kind='material' name={material?.name ?? ''}>
                <Spinner />
            </InspectorShell>
        );
    }

    const kindOfMaterial = material.getClassName?.() ?? T._('Material', 'Admin');

    // A QUALI OGGETTI È ADDOSSO. È la domanda che ci si fa guardando un materiale — «se lo cambio,
    // che cosa ridipingo?» — e la risposta la sa il motore: `getMeshesUsingMaterial` guarda anche
    // dentro i MultiMaterial, cioè le mesh che ne portano più d'uno e di cui questo è una parte.
    //
    // SI RICONTA A OGNI DISEGNO, e non è uno spreco: è un giro sulle mesh della scena. Una lista
    // tenuta da parte direbbe invece il conto di prima, perché l'assegnazione si cambia da un ALTRO
    // pannello — lo slot di una mesh — e da lì nessuno viene ad avvisare questo.
    //
    // ZERO OGGETTI, NIENTE PASTIGLIA: «0 objects» è la risposta a una domanda che nessuno ha fatto.
    const meshesUsingMaterial: any[] = sceneEditor?.getMeshesUsingMaterial?.(material.id) ?? [];

    const usedBy: InspectorUsage[] | undefined = meshesUsingMaterial.length
        ? [
              {
                  label: stringFormat(
                      meshesUsingMaterial.length === 1 ? T._('{0} object', 'Admin') : T._('{0} objects', 'Admin'),
                      meshesUsingMaterial.length.toString()
                  ),
                  current: meshesUsingMaterial.find((mesh) => sceneEditor?.isMeshSelected?.(mesh))?.id,
                  entries: meshesUsingMaterial.map((mesh): PropertiesPanelPickerOption => {
                      // I TRIANGOLI SONO UNA DIVISIONE, non un dato da chiedere a qualcuno: gli indici
                      // vanno a tre a tre. Ed è il dato che distingue due «Zone» che si chiamano quasi
                      // uguale — quale delle due è il pezzo grande.
                      const indices = typeof mesh.getTotalIndices === 'function' ? mesh.getTotalIndices() : 0;
                      const triangles = indices ? Math.round(indices / 3) : 0;

                      return {
                          value: mesh.id,
                          label: mesh.name,
                          caption: triangles ? `${triangles.toLocaleString()} ${T._('triangles', 'Admin')}` : undefined,
                          mark: <MeshNodeIcon />
                      };
                  }),
                  // ANDARCI SONO DUE MOVIMENTI: la colonna di sinistra torna sugli oggetti e la riga
                  // giusta si apre. Li fa `revealTarget` — lo stesso giro della freccia «dove sta» —
                  // e qui si dice soltanto dove si vuole andare.
                  onPick: (value) => onRevealObject?.(String(value))
              }
          ]
        : undefined;

    // Se è un materiale Standard, mostra solo il messaggio di non supportato
    // La sfera fotografata è la stessa che la lista mostra nella tessera: qui si vede com'è venuta,
    // e premendola si riscatta dopo aver cambiato il materiale.
    const portrait = (
        <PreviewThumb
            type='button'
            $image={preview}
            $color={(material as any)?.albedoColor?.toHexString?.()}
            title={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            aria-label={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            data-busy={isCapturingPreview}
            onClick={capturePreview}
            disabled={isCapturingPreview || !sceneEditor}
        >
            <PreviewShot>
                <CamerasIcon />
            </PreviewShot>
        </PreviewThumb>
    );

    if (isStandardMaterial(material)) {
        return (
            <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange} glyph={portrait}>
                <UnsupportedNote>{T._('Materiale non supportato', 'Admin')}</UnsupportedNote>
            </InspectorShell>
        );
    }

    // LE PROPRIETÀ SONO QUELLE DI SEMPRE, RIMESSE IN ORDINE. `buildPbrMaterialPanel` non le
    // costruisce: prende quelle che materialsPropertiesConfig costruisce già e le colloca nei
    // capitoli. Ciò che la tabella non nomina non compare — vedi pbrMaterialLayout.ts.
    const properties = materialsPropertiesCreator(material, helpers);

    // LA RIORGANIZZAZIONE È DEL SOLO `PBRMaterial` (utente, 2026-08-20). Gli altri due tipi PBR
    // hanno proprietà con altri nomi: passarli da questa tabella non li riorganizzerebbe, li
    // svuoterebbe — nessun id combacia, e resterebbe un pannello vuoto. «Ignorarli» vuol dire
    // lasciarli come sono, quindi si mostra l'elenco piatto di prima.
    const isClassicPbr = material instanceof BABYLON.PBRMaterial;
    const finish = finishRows(material, finishMode, () => {
        updateState(material);
        onSceneChanged?.();
    });
    const sections = isClassicPbr
        ? buildPbrMaterialPanel(properties, [...buildPbrExtraProperties(material as BABYLON.PBRMaterial, helpers), ...finish.extra], {
              finish: finishMode,
              switcher: {
                  type: 'segmented',
                  id: 'finish-mode',
                  options: [
                      { value: 'orm', label: 'ORM' },
                      { value: 'separate', label: T._('Separate', 'Admin') }
                  ],
                  value: finishMode,
                  onChange: (value: string) => changeFinishMode(value as FinishMode),
                  ariaLabel: T._('Finish maps', 'Admin')
              },
              // LE RIGHE DEL PACCHETTO SI DISEGNANO SOLO SE UN PACCHETTO C'È: la mappa del solo metallo
              // occupa lo stesso campo (vedi `packSlotIsEmpty`), e mostrarla qui direbbe che il flusso
              // ORM è pieno mentre le mappe stanno nelle Separate. Resta la riga per caricarne una —
              // vuota — e spariscono miniatura, cestino, versione mobile e scalatura.
              //
              // LE LETTERE DEI CANALI arrivano da `finishRows`.
              //
              // E LE LETTERE DELL'INVERSIONE SONO PULSANTI anche loro: la spunta del generatore da cui
              // nascono non emette più `change`, e il «da salvare» si segna da qui.
              overrides: {
                  ...(finishMode === 'orm' && packSlotIsEmpty(material) ? hiddenPackRows() : {}),
                  ...finish.overrides,
                  ...Object.fromEntries(
                      ['invert-normal-map-X', 'invert-normal-map-Y'].map((id) => [
                          id,
                          (property: PropertiesPanelLeafConfig) =>
                              property.type === 'checkbox'
                                  ? {
                                        ...property,
                                        onChange: (checked: boolean) => {
                                            property.onChange?.(checked);
                                            onSceneChanged?.();
                                        }
                                    }
                                  : property
                      ])
                  )
              },
              // SCALATURA E CANALE UV DIVENTANO IL MAPPING DELLA TEXTURE — vedi `textureMapping`. Il menu
              // del canale UV non emette `change`, quindi il «da salvare» si segna da qui.
              mapping: (type) =>
                  textureMapping(material, type, () => {
                      updateState(material);
                      onSceneChanged?.();
                  })
          })
        : [];
    // MENTRE SI CERCA NON C'È PIÙ UN «ADVANCED»: la spina nasconde tre capitoli su quattro, e una
    // ricerca che risponde «niente» perché la risposta stava dietro una maniglia chiusa è una
    // ricerca che mente. Chi cerca ha già detto cosa vuole vedere — si mostra tutto quello che
    // risponde, e alla parola cancellata il pannello torna com'era.
    const searching = parameterFilter.trim().length > 0;
    const found = searching ? filterPbrMaterialPanel(sections, parameterFilter) : sections;
    const plainSections = searching ? found : found.filter((section) => !section.advanced);
    const advancedSections = searching ? [] : found.filter((section) => section.advanced);

    return (
        <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange} glyph={portrait}>
            {/* IN EVIDENZA RESTA LA SOLA SURFACE — colore, finitura, trasparenza: ciò che si
                tocca a ogni materiale. Gli altri capitoli entrano interi sotto la spina, e il
                pannello parte con tre gruppi invece che con diciotto. */}
            {!isClassicPbr && <PropertiesPanel properties={properties} />}

            {/* CERCARE NON È MODIFICARE: `data-view-only` dice a chi ascolta le modifiche, sopra
                il palco, che da qui non esce niente da salvare. */}
            {isClassicPbr && (
                <ParameterSearch data-view-only='true'>
                    <SearchInput
                        placeholder={T._('Search parameters...', 'Admin')}
                        aria-label={T._('Search parameters', 'Admin')}
                        value={parameterFilter}
                        onChange={(event) => setParameterFilter(event.currentTarget.value)}
                    />
                </ParameterSearch>
            )}

            {/* IL PANNELLO SI RIMONTA A OGNI PAROLA CERCATA, ed è voluto: quello che risponde deve
                aprirsi, e una card decide se nascere aperta solo quando nasce. */}
            {plainSections.map((section) => (
                <PropertiesPanelSection key={section.id} title={section.label}>
                    <PropertiesPanel key={parameterFilter} properties={section.groups} />
                </PropertiesPanelSection>
            ))}

            {searching && found.length === 0 && <NoMatch>{T._('No parameter with this name', 'Admin')}</NoMatch>}

            {advancedSections.length > 0 && (
                <AdvancedSpine type='button' $open={advancedOpen} aria-expanded={advancedOpen} onClick={toggleAdvanced}>
                    <ChevronIcon />
                    <span>{T._('Advanced', 'Admin')}</span>
                </AdvancedSpine>
            )}

            {advancedOpen &&
                advancedSections.map((section) => (
                    <PropertiesPanelSection key={section.id} title={section.label}>
                        <PropertiesPanel properties={section.groups} />
                    </PropertiesPanelSection>
                ))}
        </InspectorShell>
    );
};

export default MaterialOverlaySimple;
